/** Pre-orders, and the card that turns on alerts for them. */
const preorders = {
  en: {
    title: "Pre-Orders",
    nothingWaiting: "Nothing waiting to be collected.",
    waiting: {
      one: "{count} waiting to be collected",
      other: "{count} waiting to be collected",
    },
    loadFailed: "Could not load pre-orders.",
    updateFailed: "Could not update that order.",
    empty:
      "No pre-orders yet. When a customer reserves from your menu, it shows up here.",
    statusAria: "Order status",
    status: {
      pending: "Pending",
      ready: "Ready",
      completed: "Completed",
      cancelled: "Cancelled",
    },
    customer: "Customer",
    pickupAt: "{day} at {time}",
    late: {
      one: "Not collected — {count} day late",
      other: "Not collected — {count} days late",
    },
    offersSaving: "Offers −{amount}",
    offerFallback: "Offer",
    placedToday: "Placed today at {time}",
    placedYesterday: "Placed yesterday at {time}",
    placedOn: "Placed on {day} at {time}",
    alerts: {
      title: "Know the moment a customer reserves",
      body: "This device shows a notification when a pre-order comes in: who it is for, what it comes to and when they are collecting. Nothing else.",
      iosBody:
        "On iPhone, notifications only work once the dashboard is on the home screen. Tap Share, then Add to Home Screen, and open it from there.",
      gotIt: "Got it",
      turnOn: "Turn on alerts",
      notNow: "Not now",
      confirmHint: "Your browser will ask you to confirm.",
      on: "🔔 This device will now show a notification when a pre-order comes in.",
      show: "🔔 Get notified of new pre-orders",
      denied:
        "This browser is blocking notifications for the dashboard. Allow them in the site settings, then try again.",
      failed: "Could not turn alerts on for this device just now.",
    },
  },

  fr: {
    title: "Précommandes",
    nothingWaiting: "Aucune commande à retirer.",
    waiting: {
      one: "{count} commande à retirer",
      other: "{count} commandes à retirer",
    },
    loadFailed: "Impossible de charger les précommandes.",
    updateFailed: "Impossible de mettre à jour cette commande.",
    empty:
      "Aucune précommande pour l'instant. Quand un client réserve depuis votre menu, elle apparaît ici.",
    statusAria: "Statut de la commande",
    status: {
      pending: "En attente",
      ready: "Prête",
      completed: "Terminée",
      cancelled: "Annulée",
    },
    customer: "Client",
    pickupAt: "{day} à {time}",
    late: {
      one: "Non retirée — {count} jour de retard",
      other: "Non retirée — {count} jours de retard",
    },
    offersSaving: "Offres −{amount}",
    offerFallback: "Offre",
    placedToday: "Passée aujourd'hui à {time}",
    placedYesterday: "Passée hier à {time}",
    placedOn: "Passée le {day} à {time}",
    alerts: {
      title: "Soyez prévenu dès qu'un client réserve",
      body: "Cet appareil affiche une notification à chaque nouvelle précommande : pour qui, quel montant et quand le client passe. Rien d'autre.",
      iosBody:
        "Sur iPhone, les notifications ne fonctionnent qu'une fois le tableau de bord ajouté à l'écran d'accueil. Touchez Partager, puis Sur l'écran d'accueil, et ouvrez-le depuis là.",
      gotIt: "Compris",
      turnOn: "Activer les alertes",
      notNow: "Plus tard",
      confirmHint: "Votre navigateur vous demandera de confirmer.",
      on: "🔔 Cet appareil affichera désormais une notification à chaque nouvelle précommande.",
      show: "🔔 Être prévenu des nouvelles précommandes",
      denied:
        "Ce navigateur bloque les notifications du tableau de bord. Autorisez-les dans les réglages du site, puis réessayez.",
      failed: "Impossible d'activer les alertes sur cet appareil pour le moment.",
    },
  },

  ar: {
    title: "الطلبات المسبقة",
    nothingWaiting: "لا توجد طلبات بانتظار الاستلام.",
    waiting: {
      one: "طلب واحد بانتظار الاستلام",
      two: "طلبان بانتظار الاستلام",
      few: "{count} طلبات بانتظار الاستلام",
      many: "{count} طلباً بانتظار الاستلام",
      other: "{count} طلب بانتظار الاستلام",
    },
    loadFailed: "تعذّر تحميل الطلبات المسبقة.",
    updateFailed: "تعذّر تحديث هذا الطلب.",
    empty:
      "لا توجد طلبات مسبقة بعد. عندما يحجز زبون من قائمتك، سيظهر طلبه هنا.",
    statusAria: "حالة الطلب",
    status: {
      pending: "قيد الانتظار",
      ready: "جاهز",
      completed: "مكتمل",
      cancelled: "ملغى",
    },
    customer: "زبون",
    pickupAt: "{day} الساعة {time}",
    late: {
      one: "لم يُستلم — متأخر يوماً واحداً",
      two: "لم يُستلم — متأخر يومين",
      few: "لم يُستلم — متأخر {count} أيام",
      other: "لم يُستلم — متأخر {count} يوماً",
    },
    offersSaving: "العروض −{amount}",
    offerFallback: "عرض",
    placedToday: "طُلب اليوم الساعة {time}",
    placedYesterday: "طُلب أمس الساعة {time}",
    placedOn: "طُلب يوم {day} الساعة {time}",
    alerts: {
      title: "اعرف فور حجز أي زبون",
      body: "يعرض هذا الجهاز إشعاراً عند وصول طلب مسبق: لمن هو، وقيمته، وموعد استلامه. لا شيء غير ذلك.",
      iosBody:
        "على iPhone، لا تعمل الإشعارات إلا بعد إضافة لوحة التحكم إلى الشاشة الرئيسية. اضغط مشاركة، ثم إضافة إلى الشاشة الرئيسية، وافتحها من هناك.",
      gotIt: "حسناً",
      turnOn: "تفعيل التنبيهات",
      notNow: "ليس الآن",
      confirmHint: "سيطلب منك المتصفح التأكيد.",
      on: "🔔 سيعرض هذا الجهاز الآن إشعاراً عند وصول كل طلب مسبق.",
      show: "🔔 تلقَّ إشعارات الطلبات المسبقة الجديدة",
      denied:
        "هذا المتصفح يحظر إشعارات لوحة التحكم. اسمح بها من إعدادات الموقع ثم أعد المحاولة.",
      failed: "تعذّر تفعيل التنبيهات على هذا الجهاز حالياً.",
    },
  },
};

export default preorders;
